import { Component, type ErrorInfo, type ReactNode } from 'react'
import { captureException } from '@/lib/observability'
import { t } from '@/lib/i18n'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  error: Error | null
}

// ErrorBoundary catches render-time errors in its subtree so a single broken
// component can't take down the whole SPA. Top-level wraps the router so a
// crash inside any route still leaves the shell (sidebar, toasts) usable.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    captureException(error)
    // Console is the lowest-effort sink. A future revision can forward to
    // Sentry/Highlight here without changing every call site.
    console.error('ErrorBoundary caught:', error, info.componentStack)
  }

  reset = () => this.setState({ error: null })

  render() {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback
      return (
        <div
          role="alert"
          style={{
            minHeight: '60vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            textAlign: 'center',
            color: 'var(--text)',
          }}
        >
          <div style={{ maxWidth: 420 }}>
            <div
              aria-hidden="true"
              style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}
            >
              ⚠
            </div>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.5rem' }}>
              {t('errorBoundaryTitle')}
            </h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
              {t('errorBoundaryBody')}
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={this.reset}
                style={{
                  backgroundColor: 'var(--accent)',
                  color: 'var(--on-accent)',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                }}
              >
                {t('retry')}
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                style={{
                  backgroundColor: 'transparent',
                  color: 'var(--text)',
                  border: '1px solid var(--border)',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                }}
              >
                {t('reloadApp')}
              </button>
            </div>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
